<template>
  <div v-loading="!isLoaded">
    <tf-table
      v-if="isLoaded && !error"
      v-test:table="'active events'"
      v-bind="{
        parentalControls: true,
        tableData,
        tableMeta,
      }"
      @changeSortCriteria="$emit('changeSortCriteria', $event)"
    >
      <template #title>
        Active events
      </template>
      <template
        v-if="error"
        #error
      >
        {{ error }}
      </template>
      <template #parentRow="{ rowData }">
        <active-events-row :row-data="rowData" />
      </template>
      <empty-table-cell slot="empty">
        There are no active RFPs.
      </empty-table-cell>
    </tf-table>
  </div>
</template>

<script>
  // vuex
  import { mapState } from 'vuex';
  // components
  import ActiveEventsRow from '@/components/Employer/Tables/ActiveEventsRow';
  import EmptyTableCell from '@/components/EmptyTableCell';

  /**
   * 'TableActiveEvents' is a component relying on the store data to render a table for this employer's active events (or RFPs).
   *
   * @vuedoc
   * @exports Administration/Tables/ActiveEventss
   * @category Components
   */
  export default {
    name: 'TableActiveEvents',
    components: { ActiveEventsRow, EmptyTableCell },
    props: {
      /**
       * The ID of the employer used to interact with the API.
       */
      clientId: {
        type: [String, Number],
        required: true,
      },
      /**
       * The sort direction from the parent to be used in the child table component.
       */
      sortDirection: {
        type: String,
        default: 'asc',
      },
      /**
       * The sort property from the parent to be used in the child table component.
       */
      sortProp: {
        type: String,
        default: 'type',
      },
    },
    computed: {
      ...mapState({
        isLoaded: (state) => state.activeEvents.isLoaded,
        error: (state) => state.activeEvents.error,
      }),
      /**
       * Parse the activeEvents store data to be consumed by TfTable.
       *
       * The 'products' array in the API returns all products so there will be duplicates that we create a unique array of products for. In order to allow for sorting, we need to also sort alphabetically and then join with `<br>` so the first product is the target of the sort order.
       *
       * At the moment, there are only 2 types that need to be translated to user friendly text:
       * 1) Projects::RfpPlacementEvent -> "Full marketing"
       * 2) Projects::RenewalPlacementEvent -> "Renewal collection"
       *
       * @returns {Array}
       */
      tableData() {
        return this.$store.state.activeEvents.activeEvents.map(({
          due_date: date,
          completion_risk,
          id,
          products,
          type,
          state,
        }) => ({
          date,
          completion_risk,
          id,
          type: type === 'Projects::RfpPlacementEvent'
            ? 'Full marketing'
            : 'Renewal collection',
          products: [
            ...new Set(products.map(({ product_type_name: name }) => name)),
          ].sort(),
          state,
        }));
      },
      /**
       * Return an object to pass to the 'tableMeta' tf-table. The sort parameters are computed based on the instance props.
       *
       * @returns {object}
       */
      tableMeta() {
        return {
          columns: [
            {
              format: 'type',
              label: 'Event type',
              prop: 'type',
              sortable: true,
              width: 375,
            },
            {
              format: 'products',
              label: 'Product(s)',
              prop: 'products',
            },
            {
              format: 'date',
              label: 'Due date',
              prop: 'date',
              sortable: true,
              width: 295,
            },
          ],
          sort: {
            direction: this.sortDirection,
            prop: this.sortProp,
          },
        };
      },
    },
  };
</script>

<style scoped lang="scss">
  p {
    margin: 0;
  }
</style>
